/**
 * `src/platform/` — error-message extraction (D-15′). **This file is the sole copy.**
 *
 * ⛔ **MEASURED rather than assumed.** The table below was executed against the property
 * *"extract a human message, and fall back when there isn't one"*, and it is the reason
 * `displayMessage` exists:
 *
 * ```
 *   input                              errorMessage        apiErrorMessage
 *   Error('network down')              "network down"      "network down"
 *   {body:{message:'Forbidden…'}}      <fallback>          "Forbidden by policy"   <- why apiErrorMessage exists
 *   {body:{message:['a','b']}}         <fallback>          "a; b"                  <- the strict-400 shape
 *   'boom' / null / undefined          <fallback>          <fallback>
 *   {body:{message:[]}}                <fallback>          <fallback>
 *   ⛔ Error('')                        ""                  ""                      <- BLANK
 *   ⛔ Error('   ')                     "   "               "   "                   <- BLANK
 * ```
 *
 * ⇒ **Seven of nine shapes are correct. Exactly one defect class**: an `Error` whose `message` is
 * empty or whitespace returns that blank string instead of the fallback, because the implementation
 * is `err instanceof Error ? err.message : fallback` — it tests for the WRAPPER, not for a message.
 * Every surface here renders the result into an alert box, so the outcome is a VISIBLE, EMPTY
 * `role="alert"`: the operator is told something failed and given no words, which reads as a broken
 * page rather than an error.
 *
 * ⛔ **`apiErrorMessage` deliberately does not fix the blank case.** `displayMessage` is the seam
 * every surface calls, so the blank-alert class is closed once at the boundary rather than in nine
 * render sites. `apiErrorMessage`'s handling of the two `body` shapes is worth keeping as-is:
 * `ApiError.message` is only ever `"API error <status>"`, so a surface rendering `err.message`
 * shows the status code and discards the sentence that would have told the operator what to fix.
 */
import { onboardingRefusalSchema, type OnboardingRefusal, type RolloutBlock } from '@zion/sdk-contracts';
import { displayUntrusted } from './untrusted-text';

/**
 * Extract a message from an unknown error value.
 * Use in try/catch blocks: `setError(errorMessage(err, 'Failed to save'))`.
 */
export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Human message from an API failure. `ApiError.message` is only "API error <status>";
 * the server's text is in `ApiError.body` (`{statusCode, message}` for an exception,
 * `{message: string[]}` for the strict-validation 400). Duck-typed rather than
 * importing ApiError, so this module stays free of a dependency on the http client.
 *
 * The result passes `displayUntrusted` (plan G8, R-7): about 30 panes render it as given. Most
 * server error text has no server redactor (#2219), so this is its only control.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  return displayUntrusted(rawApiErrorMessage(err, fallback));
}

/* The text `apiErrorMessage` shows, before the display scrub. `displayMessage` scrubs it once. */
function rawApiErrorMessage(err: unknown, fallback: string): string {
  if (err !== null && typeof err === 'object' && 'body' in err) {
    const body = (err as { body: unknown }).body;
    if (body !== null && typeof body === 'object' && 'message' in body) {
      const msg = (body as { message: unknown }).message;
      if (Array.isArray(msg)) {
        return msg.filter((m): m is string => typeof m === 'string').join('; ') || fallback;
      }
      if (typeof msg === 'string' && msg.length > 0) return msg;
    }
  }
  return errorMessage(err, fallback);
}

/**
 * Create a catch handler that sets an error message on a state setter.
 * Use in promise chains: `.catch(catchSetError(setError, 'Failed to load'))`.
 */
export function catchSetError(setError: (msg: string) => void, fallback: string) {
  return (err: unknown): void => {
    setError(err instanceof Error ? err.message : fallback);
  };
}

/**
 * RFC 6749 §5.2's `error_description`, when the thrown value carries one.
 *
 * ⛔ **A SECOND ENVELOPE, not a second normaliser.** The CP authorization server's error
 * filter answers `{ error, error_description }` — the RFC shape — while every other CP
 * surface answers Nest's `{ statusCode, message, error }`. `apiErrorMessage` reads only
 * `body.message`, so it sees nothing at all on the OAuth-AS shape.
 *
 * ⚠️ Reads `error_description` and NOT `error`: the latter is a machine CODE
 * (`invalid_scope`, `access_denied`) that both envelopes happen to carry — Nest's 404
 * sets `error: 'Not Found'` — so keying on it would render a code where a sentence
 * belongs and would fire on the wrong envelope. Callers that need the code use
 * `oauthErrorCode` in `api/oauthConsent.api.ts`, which compares against a specific value
 * for exactly that reason.
 */
function oauthErrorDescription(error: unknown): string | null {
  if (error === null || typeof error !== 'object' || !('body' in error)) return null;
  const body = (error as { readonly body: unknown }).body;
  if (body === null || typeof body !== 'object' || !('error_description' in body)) return null;
  const description = (body as { readonly error_description: unknown }).error_description;
  if (typeof description !== 'string' || description.trim() === '') return null;
  return description;
}

/**
 * The one function every pane renders an error through.
 *
 * ## ⛔⛔ THE ORDER IS LOAD-BEARING — `error_description` IS CONSULTED FIRST
 *
 * Measured, not stylistic: the `ApiError` constructor calls `super(`API error ${status}`)`, so
 * `.message` is always a non-blank string. `apiErrorMessage` therefore returns `"API error 400"`
 * for any OAuth-AS failure. The placeholder rule below swaps that text for the fallback, and the
 * function returns there. So a read of `error_description` after it would never run.
 *
 * ⇒ That is precisely why every OAuth-AS 400 rendered the literal **"API error 400"**
 * while discarding the operator-facing sentence the server actually sent. Two screens
 * found it from opposite ends (06a's IdP config and 06e's OAuth clients / DCR hosts);
 * it is one arm, written once, here.
 *
 * ## ⚠️ The blank guard is a MEASURED thin spot, not a hypothetical
 *
 * `errorMessage` is `err instanceof Error ? err.message : fallback` — so `new Error('')`
 * yields `''`, and the fallback never fires. An empty `message` is not exotic:
 * `throw new Error()`, a rejected promise carrying a default-constructed `Error`, and any
 * error whose message was interpolated from an absent value all produce it.
 *
 * ⛔ Closed HERE and deliberately not in `errorMessage`/`apiErrorMessage` above. Changing
 * either of those contracts is a separate question with its own coverage consequences;
 * `displayMessage` is the seam every surface calls, so closing the class once here covers
 * all nine render sites and leaves the two primitives' documented behaviour intact.
 *
 * ## The client placeholder counts as no message
 *
 * A body with no usable text leaves `ApiError`'s own `API error <status>`. That text names only the
 * status, so the plain fallback shows in its place (D5 and R-18 of the console cleanup G1 plan,
 * #1423). `ErrorState` already shows `(HTTP <status>)` after the message, so a status in the message
 * showed twice there. A message that only starts with the same words is the server's own text and
 * stays.
 *
 * ## The result passes through `displayUntrusted`
 *
 * The text is the server's, so the result is capped at 4000 characters, secret shapes become
 * `[redacted]`, and bidi and control code points show as `<U+XXXX>` (plan
 * 2026-10-22-console-cleanup-g8, D8.1). Most server error text has no server redactor (#2219), so
 * this is its only control.
 */
export function displayMessage(error: unknown, fallback: string): string {
  return displayUntrusted(serverMessage(error, fallback));
}

/**
 * The message {@link displayMessage} shows, before the display scrub. It is `error_description`, else the
 * server's `message`, else the thrown `Error`'s message, else `fallback`. A blank text and the client
 * placeholder `API error <status>` count as no message.
 *
 * Caller: `satelliteDisplayMessage`, which masks the whole text before the cap of `displayUntrusted`
 * (review row WS-14-R7). Every other caller renders through {@link displayMessage}. The result is server
 * text: it is never shown without `displayUntrusted`. Pure.
 */
export function serverMessage(error: unknown, fallback: string): string {
  const described = oauthErrorDescription(error);
  if (described !== null) return described;
  const message = rawApiErrorMessage(error, fallback);
  return message.trim() === '' || API_ERROR_PLACEHOLDER.test(message) ? fallback : message;
}

/* The text `ApiError` sets when no body text arrived. It names only the status (D5, #1423). */
const API_ERROR_PLACEHOLDER = /^API error \d+$/;

/**
 * The third envelope: a CP refusal that names a machine reason on `error`.
 *
 * `apiErrorMessage` reads `body.message` and `oauthErrorDescription` reads
 * `body.error_description`. Neither can tell a caller which refusal arrived, and three doors now
 * answer refusals a surface must render differently: `PUT /v1/apps/:appId/channel-pins/:channelId`,
 * `POST /v1/apps/:appId/promote` and `POST /v1/apps/:appId/demote` each answer
 * `409 { error: 'pin_drifted', ... }` or `409 { error: 'render_blocked', ... }` (R-007, R-021,
 * R-040). A pin that drifted is a race the operator retries. A version the door refused is a
 * state the operator fixes, and no retry can clear it.
 *
 * It is a reader and not a renderer. It answers the body's shape and builds no sentence.
 * `AppRolloutPane` maps the shape to its own copy (R-065), because the CP's `message` is written
 * for the CLI and the MCP tools and repeats the first block's `detail` verbatim.
 *
 * It lives here rather than in the pane (R-045). `ApiError.body` is `unknown`, as `ApiError`'s
 * constructor in `platform/http/client.ts` declares it, so a property read on it inside a render
 * arm is a type error. The narrowing has five failing arms: not an `ApiError`, a non-object body,
 * a body with no `error`, and the two half-formed refusals below. The package's floor is 100% of
 * branches, and five unit arms here cost five assertions where the same five as render arms would
 * cost five mounted panes.
 */
export type RefusalBody =
  /** The optimistic-concurrency refusal. `actual` is `null` when the pinned row is gone. */
  | { readonly error: 'pin_drifted'; readonly actual: string | null }
  /** The door refusal. `blocks` is the same array `/rollout-diagnostics` returns. */
  | {
      readonly error: 'render_blocked';
      readonly message: string;
      readonly blocks: readonly RolloutBlock[];
    }
  /**
   * Every other object-form refusal, including a half-formed one of the two above.
   *
   * The three `?: undefined` members are what make the union readable. `error` alone cannot
   * discriminate this arm: its type is `string`, which is comparable to both literals, so
   * TypeScript keeps it after `body.error === 'pin_drifted'` and the payload read fails to
   * compile. Measured, not assumed: `Property 'actual' does not exist on type
   * '{ readonly error: string; }'` (TS2339).
   *
   * Declaring the payload keys as `undefined` here lets a consumer narrow on the key it is about
   * to read, with `refusal.actual !== undefined`. That is also the runtime question: a
   * `pin_drifted` body carrying no `actual` is this arm, and the caller must not render drift
   * copy for it.
   */
  | {
      readonly error: string;
      readonly actual?: undefined;
      readonly message?: undefined;
      readonly blocks?: undefined;
    };

/**
 * The CP's object-form refusal, or `null` for anything else.
 *
 * Answers `null` for a non-object body, a `null` body, a body with no `error` key, an `error` that
 * is not a string, and any thrown value that is not an object with a `body`. This is the same
 * house narrowing `oauthErrorCode` uses in `api/oauthConsent.api.ts`.
 *
 * A `render_blocked` body missing `message` or `blocks`, and a `pin_drifted` body missing
 * `actual`, answer the third arm rather than `null`. The refusal did arrive and its code is
 * readable. Only the payload the copy needs is absent, so the caller falls back to the generic
 * failure sentence instead of rendering a half-formed banner.
 *
 * Shape only. `blocks` members are not validated against `rolloutBlockSchema`: the array is handed
 * to a renderer that reads `kind`, `detail` and `configKey` and prints whatever is there, and a
 * zod parse here would drop the whole refusal because one member gained a field.
 */
export function refusalOf(error: unknown): RefusalBody | null {
  if (error === null || typeof error !== 'object' || !('body' in error)) return null;
  const body = (error as { readonly body: unknown }).body;
  if (body === null || typeof body !== 'object' || !('error' in body)) return null;
  const code = (body as { readonly error: unknown }).error;
  if (typeof code !== 'string') return null;

  if (code === 'pin_drifted' && 'actual' in body) {
    const actual = (body as { readonly actual: unknown }).actual;
    if (typeof actual === 'string') return { error: code, actual };
    if (actual === null) return { error: code, actual: null };
  }

  if (code === 'render_blocked' && 'message' in body && 'blocks' in body) {
    const message = (body as { readonly message: unknown }).message;
    const blocks = (body as { readonly blocks: unknown }).blocks;
    if (typeof message === 'string' && Array.isArray(blocks)) {
      return { error: code, message, blocks };
    }
  }

  return { error: code };
}

/**
 * The coded refusal of a tenant onboarding route, or `null` for anything else (plan
 * 2026-10-16-customer-onboarding, WS-16).
 *
 * provisioning-svc answers an intake, retry or client secret refusal with exactly `{ code, message }`
 * (`onboardingRefusalSchema`), and site-admin-api passes the body through (R-072 item 3). Neither
 * `displayMessage` nor {@link refusalOf} reads `code`: the first reads `message`, the second reads
 * `error`. The wizard needs the code to put the sentence on the field it names, and the progress pane
 * needs it to tell `REQUEST_NOT_RETRYABLE` and `CLIENT_SECRET_NOT_ACCEPTED` from other failures.
 *
 * Input: any thrown value. Output: the parsed refusal when the value is an object with a `body` that
 * parses with `onboardingRefusalSchema`, else `null`: a value with no `body`, a `null` body, the
 * strict pipe's 400, a 403 or 404 body, a code outside the contract, or a body with another key. It
 * never throws and changes nothing.
 */
export function onboardingRefusalOf(error: unknown): OnboardingRefusal | null {
  if (error === null || typeof error !== 'object' || !('body' in error)) return null;
  const parsed = onboardingRefusalSchema.safeParse((error as { readonly body: unknown }).body);
  return parsed.success ? parsed.data : null;
}
